import type {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router,
} from 'vue-router'

const AUTH_FLOW_ROUTE_NAMES = new Set([
  'auth-callback',
  'auth-error',
  'auth-redirect',
  'member-login',
])

const ROUTE_CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk [\w-]+ failed/i,
  /chunkloaderror/i,
]

const HOME_FALLBACK: RouteLocationRaw = { name: 'member-home' }

const ROUTE_FALLBACKS: Record<string, RouteLocationRaw> = {
  'member-activities': HOME_FALLBACK,
  'member-annual-bill': { name: 'member-bills' },
  'member-balance': HOME_FALLBACK,
  'member-benefits': HOME_FALLBACK,
  'member-bill-detail': { name: 'member-bills' },
  'member-bills': HOME_FALLBACK,
  'member-booking-create': HOME_FALLBACK,
  'member-booking-detail': { name: 'member-booking-requests' },
  'member-booking-requests': HOME_FALLBACK,
  'member-cashbacks': { name: 'member-balance' },
  'member-channel-redemption': { name: 'member-points-shop' },
  'member-charity': HOME_FALLBACK,
  'member-consumption-records': HOME_FALLBACK,
  'member-contact': HOME_FALLBACK,
  'member-about': HOME_FALLBACK,
  'member-gift-card': HOME_FALLBACK,
  'member-order-detail': { name: 'member-orders' },
  'member-orders': HOME_FALLBACK,
  'member-points': HOME_FALLBACK,
  'member-points-lottery': { name: 'member-points-shop' },
  'member-points-order-detail': { name: 'member-points-orders' },
  'member-points-orders': { name: 'member-points-shop' },
  'member-points-product-detail': { name: 'member-points-shop' },
  'member-points-shop': { name: 'member-points' },
  'member-profile': HOME_FALLBACK,
  'member-safe-box': HOME_FALLBACK,
  'member-service': HOME_FALLBACK,
  'merchant-detail': { name: 'merchant-list' },
  'merchant-list': HOME_FALLBACK,
}

const routeName = (route: RouteLocationNormalizedLoaded) =>
  typeof route.name === 'string' ? route.name : ''

export const isRouteChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error || '')
  return ROUTE_CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export const appendDocumentReloadNonce = (
  href: string,
  nonce = Date.now(),
) => {
  const url = new URL(href, 'https://member-h5.invalid')
  url.searchParams.set('_app_refresh', String(nonce))
  return `${url.pathname}${url.search}${url.hash}`
}

export const replaceWithFreshDocument = (href: string) => {
  window.location.replace(appendDocumentReloadNonce(href))
}

export const isSafePortalBackTarget = (
  router: Router,
  currentRoute: RouteLocationNormalizedLoaded,
  target: unknown,
) => {
  if (
    typeof target !== 'string' ||
    !target.startsWith('/') ||
    target.startsWith('//')
  ) {
    return false
  }
  const resolved = router.resolve(target)
  const resolvedName = typeof resolved.name === 'string' ? resolved.name : ''
  return Boolean(
    resolved.matched.length &&
      resolved.fullPath !== currentRoute.fullPath &&
      !AUTH_FLOW_ROUTE_NAMES.has(resolvedName),
  )
}

export const resolvePortalBackFallback = (
  router: Router,
  route: RouteLocationNormalizedLoaded,
): RouteLocationRaw => {
  const returnTo = route.query.returnTo
  if (isSafePortalBackTarget(router, route, returnTo)) {
    return String(returnTo)
  }
  if (routeName(route) === 'member-points-checkout') {
    return {
      name: 'member-points-product-detail',
      params: { id: String(route.params.id || '') },
    }
  }
  return ROUTE_FALLBACKS[routeName(route)] || HOME_FALLBACK
}

export const navigatePortalBack = async (
  router: Router,
  route: RouteLocationNormalizedLoaded,
) => {
  const historyTarget =
    typeof window === 'undefined' ? undefined : window.history.state?.back
  if (isSafePortalBackTarget(router, route, historyTarget)) {
    router.back()
    return
  }
  await router.replace(resolvePortalBackFallback(router, route))
}
